import { Link } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { Footer } from '../components/Footer';
import { useTheme } from '../contexts/ThemeContext';
import { Sun, Moon, ChevronLeft } from 'lucide-react';

const Section = ({ title, children }) => (
  <section className="space-y-3">
    <h2 className="font-heading text-xl font-semibold tracking-tight">{title}</h2>
    <div className="text-sm text-muted-foreground leading-relaxed space-y-3">{children}</div>
  </section>
);

const LegalPage = ({ kind, children }) => {
  const { theme, toggle } = useTheme();
  return (
    <div className="min-h-screen flex flex-col relative z-10" data-testid={`${kind}-page`}>
      <header className="flex items-center justify-between px-6 lg:px-12 py-6 pt-safe">
        <Link to="/" className="text-primary"><Logo size={40} /></Link>
        <button onClick={toggle} aria-label="Toggle theme" className="p-2 rounded-full text-muted-foreground hover:bg-muted hover:text-foreground">
          {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>
      </header>
      <main className="flex-1 max-w-3xl mx-auto w-full px-6 lg:px-12 py-6 lg:py-10 space-y-8">
        <Link to="/" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
          <ChevronLeft className="h-4 w-4 mr-1" /> Back home
        </Link>
        {children}
      </main>
      <Footer />
    </div>
  );
};

export const Terms = () => (
  <LegalPage kind="terms">
    <div>
      <p className="uppercase tracking-[0.2em] text-xs text-muted-foreground font-medium">Legal</p>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Terms of Use</h1>
      <p className="text-xs text-muted-foreground mt-2">Last updated: 2026</p>
    </div>

    <Section title="1. Acceptance of Terms">
      <p>By creating an account or using My Flow Habit (the &quot;Service&quot;), you agree to be bound by these Terms of Use. If you do not agree, do not use the Service.</p>
    </Section>

    <Section title="2. The Service">
      <p>My Flow Habit is a habit-tracking tool that lets you record, review, and reflect on personal habits. The Service is provided &quot;as is&quot; without warranties of any kind.</p>
      <p>You understand that any content, advice, suggestions, or templates presented in the Service constitute opinions, not professional, medical, legal, or financial advice. You should consult a qualified professional for decisions in those areas.</p>
    </Section>

    <Section title="3. Your Account">
      <p>You are responsible for keeping your account credentials secure and for all activity that occurs under your account. We use Supabase for authentication.</p>
    </Section>

    <Section title="4. Acceptable Use">
      <p>You agree not to (a) use the Service for any unlawful purpose, (b) attempt to access another user&apos;s data, (c) reverse-engineer or interfere with the Service, or (d) upload content that is harmful, abusive, or violates someone else&apos;s rights.</p>
    </Section>

    <Section title="5. Affiliate Disclosure">
      <p>If you purchase anything through a link in the Service, you should assume we have an affiliate relationship with the company providing that product or service and that we may be compensated. We recommend you do your own independent research before purchasing anything.</p>
    </Section>

    <Section title="6. Disclaimer & Limitation of Liability">
      <p>To the maximum extent permitted by law, My Flow Habit, Dr Brandt Gibson LLC, Dr Brandt Gibson, and all members and affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss of profits or revenues, whether incurred directly or indirectly, resulting from your use of the Service.</p>
      <p>You hold My Flow Habit, Dr Brandt Gibson LLC, Dr Brandt Gibson, and all members and affiliates harmless in any event or claim arising from your use of the Service.</p>
    </Section>

    <Section title="7. Changes">
      <p>We may update these Terms from time to time. Material changes will be communicated via the Service. Continued use after a change means you accept the revised Terms.</p>
    </Section>

    <Section title="8. Contact">
      <p>Questions about these Terms? Contact us through the email associated with your account.</p>
    </Section>

    <p className="text-xs text-muted-foreground pt-4 border-t border-border">
      © 2026 My Flow Habit, Dr Brandt Gibson, LLC &amp; Dr Brandt R Gibson. All Rights Reserved.
    </p>
  </LegalPage>
);

export const Privacy = () => (
  <LegalPage kind="privacy">
    <div>
      <p className="uppercase tracking-[0.2em] text-xs text-muted-foreground font-medium">Legal</p>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Privacy Policy</h1>
      <p className="text-xs text-muted-foreground mt-2">Last updated: 2026</p>
    </div>

    <Section title="1. What We Collect">
      <p>When you create an account we collect your email address and the display name you choose. When you use the Service we store the habits, logs, notes, reminder times, and (optionally) push-notification subscriptions you create.</p>
      <p>We do not sell your data, and we do not use it to train ad-targeting profiles.</p>
    </Section>

    <Section title="2. How We Use It">
      <p>Your data is used solely to provide the Service: showing your habits, calculating streaks, rendering heatmaps and statistics, generating optional shareable links you explicitly create, and (if you opt in) sending you reminder notifications.</p>
    </Section>

    <Section title="3. Where It Lives">
      <p>Your data is stored in Supabase (Postgres) with Row Level Security policies that restrict access to your own user account. Only you can read or modify your habits and logs.</p>
      <p>If you create a public share link for a single habit, that one habit and its log history become readable by anyone with the link until you revoke it. Your other habits remain private.</p>
    </Section>

    <Section title="4. Cookies & Local Storage">
      <p>We use your browser&apos;s local storage to remember your theme preference, queue offline habit logs for later sync, and keep your Supabase auth session. We do not use third-party advertising cookies.</p>
    </Section>

    <Section title="5. Notifications">
      <p>If you enable in-browser reminders, your browser asks you for permission. If you subscribe to Web Push, an endpoint URL provided by your browser&apos;s push service is stored so we can send notifications. You can unsubscribe at any time from Settings or your browser&apos;s site permissions.</p>
    </Section>

    <Section title="6. Data Export & Deletion">
      <p>You can export your data as JSON or CSV from Settings. You can delete all of your data at any time from Settings → Danger Zone. Account deletion removes your habits, logs, categories, share links, and push subscriptions immediately.</p>
    </Section>

    <Section title="7. Third Parties">
      <p>We use Supabase for authentication and database, and (optionally) browser push services for delivering reminders. We do not share your personal data with other third parties.</p>
    </Section>

    <Section title="8. Contact">
      <p>For privacy questions or to exercise any data rights, contact us through the email associated with your account.</p>
    </Section>

    <p className="text-xs text-muted-foreground pt-4 border-t border-border">
      © 2026 My Flow Habit, Dr Brandt Gibson, LLC &amp; Dr Brandt R Gibson. All Rights Reserved.
    </p>
  </LegalPage>
);
